import { JoinStreakQueueRequestV2, METRICS_EVENTS } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { joinAutomaticStreakQueueV2 } from "@/utils/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

export interface UseJoinStreakQueueOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Opts the user into the autopilot streak queue for a V2 campaign. The
 * backend picks queued users on each cron tick and materializes them into
 * real streaks when slots open up.
 */
export const useJoinStreakQueue = (options?: UseJoinStreakQueueOptions) => {
  const { auth } = useHubUser();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: async (request: JoinStreakQueueRequestV2) => {
      const result = await joinAutomaticStreakQueueV2(auth, request);

      if (isServerApiRfcError(result)) {
        throw new Error(result.detail);
      }
      if (isServerApiError(result)) {
        throw new Error(result.message);
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      trackEvent(METRICS_EVENTS.AUTOMATIC_STREAK_CREATED, {
        campaign_name: variables.campaignName,
        total_target_volume: variables.totalTargetVolume,
        version: "V2",
        queued: true,
        priority_tickets_spent: variables.priorityTicketsToSpend ?? 0,
      });

      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAKS] });
      // Match the read hook's key prefix `[AUTOMATIC_STREAK, "queue", "item"]`;
      // passing `campaignName` at index 2 mismatches "item" and invalidates nothing.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, "queue", "item"] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, "priority-tickets"] });

      enqueueSnackbar("You're in the autopilot queue. We'll start your streak as soon as a slot opens up.", {
        variant: "success",
      });

      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to join autopilot queue: ${error.message}`, {
        variant: "error",
      });

      options?.onError?.(error.message);
    },
  });

  return {
    joinStreakQueue: mutation.mutateAsync,
    isJoining: mutation.isPending,
    error: mutation.error?.message,
  };
};
